import { UserAvatar } from '@app/components/users/UserAvatar';
import { useAllUsersQuery } from '@app/components/users/useAllUsersQuery';
import { useListViewContext } from '@app/core/list-view/context/ListViewContext';
import { getAppUserDisplayName, getUserInitials } from '@app/helpers/user';
import { userRoleIconMap } from '@app/modules/admin/screens/users/components/UserRoleIconMap';
import { User, UserRole } from '@mi-front/core';
import { Autocomplete, Box, FormControl, TextField } from '@mui/material';
import { parseAsString, useQueryState } from 'nuqs';
import { MessageDescriptor, useIntl } from 'react-intl';

/**
 * Props for the generic user select filter component.
 */
export interface UserSelectFilterProps {
  /** The URL query parameter key */
  name: string;
  /** Message descriptor for the label */
  label: MessageDescriptor;
  /** Whether the filter can be removed (shows close icon) */
  removable?: boolean;
  /** Whether to exclude client admins from the user list */
  excludeClientAdmins?: boolean;
}

/**
 * Generic user select filter. Writes go through ListView setFilterValue so filter
 * changes are bundled with a page reset. Reads from nuqs (useQueryState).
 *
 * TODO: reuse UserSelectField
 */
export function UserSelectFilter({
  name,
  label,
  removable = false,
  excludeClientAdmins = false,
}: UserSelectFilterProps) {
  const { $t } = useIntl();
  const { setFilterValue } = useListViewContext();
  const [value] = useQueryState(name, parseAsString);
  const { users, isLoading } = useAllUsersQuery(excludeClientAdmins);

  const handleChange = (user: User | null) => {
    setFilterValue(name, user?.id || null);
  };

  const selectedUser = users.find((u) => u.id === value) || null;

  const sortedUsers = [...users].sort((a, b) => {
    const nameA = getAppUserDisplayName(a, 'long').toLowerCase();
    const nameB = getAppUserDisplayName(b, 'long').toLowerCase();
    return nameA.localeCompare(nameB);
  });

  const translatedLabel = $t(label);

  return (
    <FormControl size="small" sx={{ minWidth: 250 }}>
      <Autocomplete
        size="small"
        value={selectedUser}
        options={sortedUsers}
        loading={isLoading}
        onChange={(event, newValue) => {
          handleChange(newValue);
        }}
        getOptionLabel={(option: User) => getAppUserDisplayName(option, 'long')}
        isOptionEqualToValue={(option: User, value: User) => {
          return option.id === value?.id;
        }}
        slotProps={{
          paper: {
            sx: {
              // Grow past the input to the right; popper keeps input width so left edges stay aligned
              width: 'max-content',
              minWidth: '100%',
            },
          },
        }}
        renderOption={(props, option: User) => (
          <li {...props} key={option.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
              <UserAvatar size="medium" src={option.avatar} initials={getUserInitials(option)} />
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    fontWeight: 400,
                    fontSize: '0.875rem',
                  }}
                >
                  {getAppUserDisplayName(option, 'long')}
                  {option.role === UserRole.CLIENT_ADMIN && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        '& svg': {
                          fontSize: '1rem',
                        },
                      }}
                    >
                      {userRoleIconMap[UserRole.CLIENT_ADMIN]}
                    </Box>
                  )}
                </Box>
                <Box
                  sx={{
                    fontSize: '0.75rem',
                    color: '#666',
                  }}
                >
                  {option.email}
                </Box>
              </Box>
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={translatedLabel}
            placeholder={$t({
              id: 'common.actions.select',
              defaultMessage: 'Seleccionar...',
            })}
            InputProps={{
              ...params.InputProps,
              startAdornment: selectedUser ? (
                <>
                  <UserAvatar
                    size="small"
                    src={selectedUser.avatar}
                    initials={getUserInitials(selectedUser)}
                  />
                  {params.InputProps.startAdornment}
                </>
              ) : (
                params.InputProps.startAdornment
              ),
            }}
          />
        )}
      />
    </FormControl>
  );
}
